import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, Search, Shield, Trash2 } from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../config';
import { useAuth } from '../context/AuthContext';

export default function Users() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [selectedGroup, setSelectedGroup] = useState('');
  const [activeTab, setActiveTab] = useState('ACTIVE'); // 'ACTIVE' | 'INACTIVE'

  // Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ 
    email: '', password: '', name: '', role: 'CUSTOMER', 
    userGroupId: '', phone: '', status: 'ACTIVE', roleIds: [], allowRpaMode: false, allowSso: true, allowGeo: true, allowSeo: false, allowSem: false, allowFeeds: false
  });
  
  // Role Assignment Modal
  const [allRoles, setAllRoles] = useState([]);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [selectedUserForRole, setSelectedUserForRole] = useState(null);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);

  useEffect(() => {
    fetchData();
  }, [selectedGroup]);

  const fetchData = async () => {
    try {
      const [usersRes, groupsRes, rolesRes] = await Promise.all([
        fetch(`${API_URL}/admin/users${selectedGroup ? `?groupId=${selectedGroup}` : ''}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/admin/groups`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/admin/roles`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({data: []}))
      ]);
      if (usersRes.status === 401 || groupsRes.status === 401) { navigate('/login'); return; }
      const usersData = await usersRes.json();
      const groupsData = await groupsRes.json();
      setUsers(Array.isArray(usersData) ? usersData : []);
      setGroups(Array.isArray(groupsData) ? groupsData : []);
      if (rolesRes && rolesRes.data) {
        setAllRoles(rolesRes.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const url = editingUser 
      ? `${API_URL}/admin/users/${editingUser.id}`
      : `${API_URL}/admin/users`;
    
    const method = editingUser ? 'PATCH' : 'POST';

    // Remove empty password if editing
    const payload = { ...formData };
    if (editingUser && !payload.password) delete payload.password;
    if (payload.role === 'ADMIN') {
      payload.roleIds = []; // clear role bindings if legacy ADMIN is used
    }

    try {
      const res = await fetch(url, {
        method,
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        setIsModalOpen(false);
        setEditingUser(null);
        fetchData();
      } else {
        const err = await res.json();
        alert(err.error || '操作失败');
      }
    } catch (error) {
      console.error("Failed to save user", error);
    }
  };

  
  const handleDeleteUser = async (user) => {
    alert('暂不支持直接删除用户，请停用该账号。');
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setFormData({ 
      email: user.email, 
      password: '', // Don't show password
      name: user.name || '', 
      role: user.role,
      userGroupId: user.userGroupId || '',
      phone: user.phone || '',
      status: user.status,
      // Filter out super_admin role so it doesn't get persisted if downgraded
      roleIds: user.roles?.filter(r => r.name !== 'super_admin').map(r => r.id) || [],
        allowRpaMode: user.allowRpaMode || false,
        allowSso: user.allowSso !== false,
        allowGeo: user.allowGeo !== false,
        allowSeo: user.allowSeo || false,
        allowSem: user.allowSem || false,
        allowFeeds: user.allowFeeds || false,
    });
    setIsModalOpen(true);
  };

  const openCreate = () => {
    setEditingUser(null);
    setFormData({ 
      email: '', password: '', name: '', role: 'CUSTOMER', 
      userGroupId: groups.length > 0 ? groups[0].id : '', 
      phone: '', status: 'ACTIVE', roleIds: [], allowRpaMode: false, allowSso: true, allowGeo: true, allowSeo: false, allowSem: false, allowFeeds: false
    });
    setIsModalOpen(true);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">用户管理</h1>
        <button 
          onClick={openCreate}
          className="flex items-center bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          <Plus size={18} className="mr-2" /> 新增用户
        </button>
      </div>

      
      {/* Tabs */}
      <div className="flex space-x-4 mb-4 border-b border-gray-200 dark:border-slate-700">
        <button
          className={`pb-2 px-2 text-sm font-medium transition-colors border-b-2 ${
            activeTab === 'ACTIVE'
              ? 'border-blue-600 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
          }`}
          onClick={() => setActiveTab('ACTIVE')}
        >
          活跃用户
        </button>
        <button
          className={`pb-2 px-2 text-sm font-medium transition-colors border-b-2 ${
            activeTab === 'INACTIVE'
              ? 'border-blue-600 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
          }`}
          onClick={() => setActiveTab('INACTIVE')}
        >
          已停用账号
        </button>
      </div>

      {/* Filters */}
      <div className="mb-6 flex gap-4">
        <div className="relative">
          <select 
            value={selectedGroup} 
            onChange={e => setSelectedGroup(e.target.value)}
            className="appearance-none bg-white text-gray-900 dark:bg-slate-800 border border-gray-200 dark:border-slate-600 dark:text-gray-200 rounded-lg px-4 py-2 pr-8 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option className="bg-white text-gray-900 dark:bg-slate-800 dark:text-gray-200" value="">所有用户组</option>
            {groups.map(g => (
              <option className="bg-white text-gray-900 dark:bg-slate-800 dark:text-gray-200" key={g.id} value={g.id}>{g.name}</option>
            ))}
          </select>
          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
            <Search size={16} className="text-gray-400" />
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800/50 border border-gray-100 dark:border-slate-700 rounded-xl shadow overflow-hidden">
        <table className="w-full text-left border-collapse">
          <thead className="bg-gray-50 dark:bg-slate-800/80 border-b border-gray-100 dark:border-slate-700">
            <tr>
              <th className="p-4 text-sm font-semibold text-gray-600 dark:text-gray-400">用户</th>
              <th className="p-4 text-sm font-semibold text-gray-600 dark:text-gray-400">所属组</th>
              <th className="p-4 text-sm font-semibold text-gray-600 dark:text-gray-400">联系电话</th>
              <th className="p-4 text-sm font-semibold text-gray-600 dark:text-gray-400 w-[30%] max-w-[300px]">角色</th>
              <th className="p-4 text-sm font-semibold text-gray-600 dark:text-gray-400 min-w-[80px] whitespace-nowrap">状态</th>
              <th className="p-4 text-sm font-semibold text-gray-600 dark:text-gray-400 min-w-[80px] whitespace-nowrap">任务数</th>
              <th className="p-4 text-sm font-semibold text-gray-600 dark:text-gray-400 min-w-[80px] whitespace-nowrap">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-slate-700 text-sm">
            {users.filter(u => u.status === activeTab).map(u => (
              <tr key={u.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/30">
                <td className="p-3">
                  <div className="font-medium text-gray-900 dark:text-gray-200">{u.name || '未命名'}</div>
                  <div className="text-sm text-gray-500">{u.email}</div>
                </td>
                <td className="p-3 text-gray-600 dark:text-gray-400">{u.userGroup?.name || '-'}</td>
                <td className="p-3 text-gray-600 dark:text-gray-400">{u.phone || '-'}</td>
                <td className="p-3">
                  <div className="flex flex-wrap gap-1">
                    {u.role === 'ADMIN' && <span className="px-2 py-1 bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300 rounded text-[10px] font-bold">超级管理员</span>}
                    {u.allowRpaMode && <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded text-[10px] font-bold">RPA模式</span>}
                    {u.allowSso === false && <span className="px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 rounded text-[10px] font-bold">禁止SSO</span>}
                    {u.roles && u.roles.map(r => (
                      <span key={r.id} className="px-2 py-1 bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300 rounded text-sm">
                        {r.displayName}
                      </span>
                    ))}
                    {(!u.roles || u.roles.length === 0) && u.role !== 'ADMIN' && (
                      <span className="px-2 py-1 bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300 rounded text-sm">普通用户</span>
                    )}
                  </div>
                </td>
                <td className="p-3">
                  <span className={`px-2 py-1 rounded text-sm ${u.status === 'ACTIVE' ? 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-300' : 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300'}`}>
                    {u.status === 'ACTIVE' ? '正常' : '禁用'}
                  </span>
                </td>
                <td className="p-3 text-gray-600 dark:text-gray-400">{u._count?.tasks || 0}</td>
                <td className="p-3">
                  <button onClick={() => openEdit(u)} className="text-blue-600 hover:bg-blue-50 p-2 rounded" title="编辑">
                    <Edit2 size={14} />
                  </button>
                  <button onClick={() => handleDeleteUser(u)} className="text-red-500 hover:bg-red-50 p-2 rounded ml-2" title="删除">
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800/80 backdrop-blur-xl border border-gray-200 dark:border-slate-700 p-6 rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">{editingUser ? '编辑用户' : '新增用户'}</h2>
            <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">所属用户组 *</label>
                <select 
                  className="w-full border dark:border-slate-600 dark:bg-slate-700/50 dark:text-gray-200 rounded-lg p-2"
                  value={formData.userGroupId}
                  onChange={e => setFormData({...formData, userGroupId: e.target.value})}
                  required
                >
                  <option className="bg-white text-gray-900 dark:bg-slate-800 dark:text-gray-200" value="" disabled>选择用户组</option>
                  {groups.map(g => (
                    <option className="bg-white text-gray-900 dark:bg-slate-800 dark:text-gray-200" key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">邮箱 *</label>
                <input 
                  type="email" 
                  className="w-full border dark:border-slate-600 dark:bg-slate-700/50 dark:text-gray-200 rounded-lg p-2"
                  autoComplete="off" value={formData.email}
                  onChange={e => setFormData({...formData, email: e.target.value})}
                  required
                  disabled={!!editingUser}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">姓名</label>
                <input 
                  type="text" 
                  className="w-full border dark:border-slate-600 dark:bg-slate-700/50 dark:text-gray-200 rounded-lg p-2"
                  value={formData.name}
                  onChange={e => setFormData({...formData, name: e.target.value})}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">密码 {editingUser && '(留空不修改)'}</label>
                <input 
                  type="password" 
                  className="w-full border dark:border-slate-600 dark:bg-slate-700/50 dark:text-gray-200 rounded-lg p-2"
                  autoComplete="new-password" value={formData.password}
                  onChange={e => setFormData({...formData, password: e.target.value})}
                  required={!editingUser}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">电话</label>
                <input 
                  type="text" 
                  className="w-full border dark:border-slate-600 dark:bg-slate-700/50 dark:text-gray-200 rounded-lg p-2"
                  autoComplete="new-password" value={formData.phone}
                  onChange={e => setFormData({...formData, phone: e.target.value})}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">系统身份</label>
                <select 
                  className="w-full border dark:border-slate-600 dark:bg-slate-700/50 dark:text-gray-200 rounded-lg p-2"
                  value={formData.role}
                  onChange={e => setFormData({...formData, role: e.target.value})}
                >
                  <option className="bg-white text-gray-900 dark:bg-slate-800 dark:text-gray-200" value="CUSTOMER">标准用户 (按角色分配权限)</option>
                  <option className="bg-white text-gray-900 dark:bg-slate-800 dark:text-gray-200" value="ADMIN">超级管理员 (拥有所有权限)</option>
                </select>
              </div>
              
              {formData.role === 'CUSTOMER' && (
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">分配角色权限</label>
                  <div className="grid grid-cols-2 gap-2 border dark:border-slate-600 p-3 rounded-lg max-h-40 overflow-y-auto bg-gray-50 dark:bg-slate-700/50">
                    {allRoles.filter(r => r.name !== 'super_admin').map(role => (
                      <label key={role.id} className="flex items-center space-x-2 text-sm text-gray-700 dark:text-gray-300">
                        <input 
                          type="checkbox"
                          checked={formData.roleIds.includes(role.id)}
                          onChange={e => {
                            const newRoleIds = e.target.checked 
                              ? [...formData.roleIds, role.id]
                              : formData.roleIds.filter(id => id !== role.id);
                            setFormData({...formData, roleIds: newRoleIds});
                          }}
                          className="rounded border-gray-300 text-blue-600"
                        />
                        <span>{role.displayName}</span>
                      </label>
                    ))}
                    {allRoles.length === 0 && <span className="text-gray-400 text-sm">暂无可用角色</span>}
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">状态</label>
                <select 
                  className="w-full border dark:border-slate-600 dark:bg-slate-700/50 dark:text-gray-200 rounded-lg p-2"
                  value={formData.status}
                  onChange={e => setFormData({...formData, status: e.target.value})}
                >
                  <option className="bg-white text-gray-900 dark:bg-slate-800 dark:text-gray-200" value="ACTIVE">正常</option>
                  <option className="bg-white text-gray-900 dark:bg-slate-800 dark:text-gray-200" value="INACTIVE">禁用</option>
                </select>
              </div>


              <div className="col-span-2 bg-indigo-50 dark:bg-indigo-500/10 p-3 rounded-lg border border-indigo-100 dark:border-indigo-800">
                {formData.role !== 'ADMIN' && (
                  <label className="flex items-center space-x-3 cursor-pointer mb-4">
                    <input 
                      type="checkbox"
                      className="form-checkbox h-5 w-5 text-indigo-600 rounded border-indigo-300 focus:ring-indigo-500"
                      checked={formData.allowRpaMode}
                      onChange={e => setFormData({...formData, allowRpaMode: e.target.checked})}
                    />
                    <div>
                      <span className="block text-sm font-medium text-indigo-900 dark:text-indigo-200">允许使用 RPA 深层抓取模式 (仅对非超管生效)</span>
                      <span className="block text-sm text-indigo-600/70 dark:text-indigo-400/70 mt-0.5">当全局 RPA 开启时，只有勾选此项的高级用户才能在发起任务时选择 RPA 模式</span>
                    </div>
                  </label>
                )}
                
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input 
                    type="checkbox"
                    className="form-checkbox h-5 w-5 text-indigo-600 rounded border-indigo-300 focus:ring-indigo-500"
                    checked={formData.allowSso}
                    onChange={e => setFormData({...formData, allowSso: e.target.checked})}
                  />
                  <div>
                    <span className="block text-sm font-medium text-indigo-900 dark:text-indigo-200">允许跨系统 SSO 登录</span>
                    <span className="block text-sm text-indigo-600/70 dark:text-indigo-400/70 mt-0.5">允许该用户通过顶栏九宫格无缝跳转至其他关联系统（如：智墨）</span>
                  </div>
                </label>
              </div>

              <div className="col-span-2 bg-blue-50 dark:bg-blue-500/10 p-4 rounded-lg border border-blue-100 dark:border-blue-800 mt-2">
                <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-3">系统子应用访问权限</h3>
                <div className="grid grid-cols-2 gap-4">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input 
                      type="checkbox"
                      className="form-checkbox h-5 w-5 text-blue-600 rounded border-blue-300 focus:ring-blue-500"
                      checked={formData.allowGeo}
                      onChange={e => setFormData({...formData, allowGeo: e.target.checked})}
                    />
                    <span className="text-sm font-medium text-blue-900 dark:text-blue-200">GEO 全景监控系统</span>
                  </label>
                  
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input 
                      type="checkbox"
                      className="form-checkbox h-5 w-5 text-blue-600 rounded border-blue-300 focus:ring-blue-500"
                      checked={formData.allowSeo}
                      onChange={e => setFormData({...formData, allowSeo: e.target.checked})}
                    />
                    <span className="text-sm font-medium text-blue-900 dark:text-blue-200">SEO 矩阵洞察系统</span>
                  </label>

                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input 
                      type="checkbox"
                      className="form-checkbox h-5 w-5 text-blue-600 rounded border-blue-300 focus:ring-blue-500"
                      checked={formData.allowSem}
                      onChange={e => setFormData({...formData, allowSem: e.target.checked})}
                    />
                    <span className="text-sm font-medium text-blue-900 dark:text-blue-200">SEM 竞价管理系统</span>
                  </label>

                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input 
                      type="checkbox"
                      className="form-checkbox h-5 w-5 text-blue-600 rounded border-blue-300 focus:ring-blue-500"
                      checked={formData.allowFeeds}
                      onChange={e => setFormData({...formData, allowFeeds: e.target.checked})}
                    />
                    <span className="text-sm font-medium text-blue-900 dark:text-blue-200">Feeds 信息流系统</span>
                  </label>
                </div>
              </div>

              <div className="col-span-2 flex justify-end gap-2 mt-4">
                <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 rounded-lg">取消</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">保存</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
